import React, { createContext, useCallback, useContext, useState, useEffect, useMemo } from 'react';
import {
  BrandsStep,
  PresentationsTabsPath,
  ReportTypeStep,
  ReportTypeStepSchema,
  Presentation,
  ResolvedFormConfig
} from './types';
import {
  initialBrands,
  initialSelections,
  initialType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/constants';
import { resolveFormConfig, DEFAULT_FORM_CONFIG } from './constants/formConfigs';
import { createSelectionsSchema, createBrandsSchema, DynamicSelectionsStep, shouldValidateBrandIdsOnBrandsStep, validateArrayFieldCount } from './utils/createDynamicSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useGenerateReport } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/hooks/useGenerateReport';
import { usePresentations } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/hooks/usePresentations';
import {
  getCachedPresentationFormState,
  setCachedPresentationFormBrands,
  setCachedPresentationFormSelections,
  setCachedPresentationFormType,
  scheduleCacheClear,
  cancelScheduledCacheClear
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormCache';
import { useAppSelector } from 'src/utils/Hooks';

// =============================================================================
// TESTING FLAG - Set to true to test custom form config
// =============================================================================

type StepValidity = {
  [PresentationsTabsPath.type]: boolean;
  [PresentationsTabsPath.selections]: boolean;
  [PresentationsTabsPath.brands]: boolean;
  [PresentationsTabsPath.confirmations]: boolean;
};

type PresentationFormContextType = {
  type: UseFormReturn<ReportTypeStep>;
  selections: UseFormReturn<DynamicSelectionsStep>;
  brands: UseFormReturn<BrandsStep>;
  isValidStep: (step: PresentationsTabsPath) => boolean;
  stepValidity: StepValidity;
  handleOnSubmit: () => void;
  isSubmitting: boolean;
  presentations: Presentation[];
  isPresentationsLoading: boolean;
  presentationsError: Error | null;
  /** Current form configuration based on selected presentation type */
  currentFormConfig: ResolvedFormConfig;
};

const PresentationFormContext = createContext<PresentationFormContextType | null>(null);

const PresentationFormProvider = ({ children }: { children: React.ReactNode }) => {
  const { generateReport, isPending: isSubmitting } = useGenerateReport();
  const defaultCurrency = useAppSelector((state) => state.retailer?.currencyCode || 'USD');

  const { data: presentationsData, isLoading: isPresentationsLoading, error: presentationsError } = usePresentations();

  // Initial brands step validity depends on whether brandIds needs to be validated there
  const initialBrandsValidity = !shouldValidateBrandIdsOnBrandsStep(DEFAULT_FORM_CONFIG);

  const [stepValidity, setStepValidity] = useState<StepValidity>({
    [PresentationsTabsPath.type]: false,
    [PresentationsTabsPath.selections]: false,
    [PresentationsTabsPath.brands]: initialBrandsValidity,
    [PresentationsTabsPath.confirmations]: true
  });

  // Current form configuration - starts with test config if testing, otherwise default
  const [currentFormConfig, setCurrentFormConfig] = useState<ResolvedFormConfig>(
    DEFAULT_FORM_CONFIG
  );

  // Create dynamic schemas based on current config
  const selectionsSchema = useMemo(
    () => createSelectionsSchema(currentFormConfig),
    [currentFormConfig]
  );

  const brandsSchema = useMemo(
    () => createBrandsSchema(currentFormConfig),
    [currentFormConfig]
  );

  // `defaultValues` are only read on the first render of each `useForm` —
  // we pull from the shared `PresentationFormCache` so a remount mid-session
  // (see `PresentationFormCache` doc) restores the user's prior input instead
  // of resetting to empty defaults.
  const initialCachedFormState = getCachedPresentationFormState();
  const typeForm = useForm<ReportTypeStep>({
    mode: 'all',
    resolver: zodResolver(ReportTypeStepSchema),
    defaultValues: initialCachedFormState.type ?? initialType
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectionsForm = useForm<DynamicSelectionsStep>({
    mode: 'all',
    resolver: zodResolver(selectionsSchema) as any,
    defaultValues:
      initialCachedFormState.selections ??
      ({
        currency: defaultCurrency,
        retailerIds: [],
        entityType: initialSelections.entityType,
        entityId: initialSelections.entityId,
        categoryIds: [],
        subcategoryIds: [],
        brandIds: []
      } as DynamicSelectionsStep)
  });

  const brandsForm = useForm<BrandsStep>({
    mode: 'all',
    resolver: zodResolver(brandsSchema),
    defaultValues: initialCachedFormState.brands ?? initialBrands
  });

  // Watch for report type changes and update form config
  const selectedReportType = typeForm.watch('reportType');

  useEffect(() => {
    if (!selectedReportType?.id) {
      setCurrentFormConfig(DEFAULT_FORM_CONFIG);
      return;
    }

    // Find the selected presentation from the list
    const selectedPresentation = presentationsData?.find(
      (p) => p.presentationId === selectedReportType.id
    );

    // Resolve the form config (handles validation and merging with defaults)
    const resolvedConfig = resolveFormConfig(selectedPresentation?.formConfig);
    setCurrentFormConfig(resolvedConfig);
  }, [selectedReportType?.id, presentationsData]);

  // =============================================================================
  // CENTRALIZED VALIDATION FUNCTIONS
  // =============================================================================
  
  const validateTypeStep = useCallback(() => {
    return ReportTypeStepSchema.safeParse(typeForm.getValues()).success;
  }, [typeForm]);

  const validateSelectionsStep = useCallback(() => {
    const values = selectionsForm.getValues();
    const result = selectionsSchema.safeParse(values);
    if (!result.success) {
      console.log('[Selections Validation] Failed:', result.error.issues);
      console.log('[Selections Validation] Values:', values);
      console.log('[Selections Validation] Config:', currentFormConfig);
    }
    return result.success;
  }, [selectionsForm, selectionsSchema, currentFormConfig]);

  const validateBrandsStep = useCallback(() => {
    const brandsValid = brandsSchema.safeParse(brandsForm.getValues()).success;
    const validateBrandIdsHere = shouldValidateBrandIdsOnBrandsStep(currentFormConfig);
    
    if (validateBrandIdsHere) {
      const brandIds = selectionsForm.getValues('brandIds') || [];
      const { minCount, maxCount } = currentFormConfig.brandIds;
      const brandIdsValid = validateArrayFieldCount(brandIds, minCount ?? 1, maxCount);
      return brandsValid && brandIdsValid;
    }
    return brandsValid;
  }, [brandsForm, brandsSchema, selectionsForm, currentFormConfig]);

  const revalidateAllSteps = useCallback(() => {
    setStepValidity((prev) => ({
      ...prev,
      [PresentationsTabsPath.type]: validateTypeStep(),
      [PresentationsTabsPath.selections]: validateSelectionsStep(),
      [PresentationsTabsPath.brands]: validateBrandsStep()
    }));
  }, [validateTypeStep, validateSelectionsStep, validateBrandsStep]);

  // =============================================================================
  // VALIDATION EFFECT
  // =============================================================================

  // Subscribe to form changes and validate steps
  // Re-runs when schemas change (via validation function dependencies), which handles presentation type switches
  useEffect(() => {
    // Trigger form validation for react-hook-form's internal state
    selectionsForm.trigger();
    brandsForm.trigger();
    
    // Run initial validation (handles schema changes / presentation type switches)
    revalidateAllSteps();

    // Set up watchers for form value changes
    const typeSubscription = typeForm.watch(() => {
      setStepValidity((prev) => ({ ...prev, [PresentationsTabsPath.type]: validateTypeStep() }));
    });

    const selectionsSubscription = selectionsForm.watch(() => {
      setStepValidity((prev) => ({
        ...prev,
        [PresentationsTabsPath.selections]: validateSelectionsStep(),
        // Re-validate brands too since brandIds might be on selections form
        [PresentationsTabsPath.brands]: validateBrandsStep()
      }));
    });

    const brandsSubscription = brandsForm.watch(() => {
      setStepValidity((prev) => ({ ...prev, [PresentationsTabsPath.brands]: validateBrandsStep() }));
    });

    return () => {
      typeSubscription.unsubscribe();
      selectionsSubscription.unsubscribe();
      brandsSubscription.unsubscribe();
    };
  }, [typeForm, selectionsForm, brandsForm, validateTypeStep, validateSelectionsStep, validateBrandsStep, revalidateAllSteps]);

  // Mirror form values into `PresentationFormCache` so a transient remount
  // (HomeAuthLayout loading cycle) doesn't drop the user's input.
  //
  // On mount we cancel any pending cache clear (proving this is a transient
  // remount, not a fresh visit). On unmount we schedule a deferred clear —
  // if the provider remounts quickly the clear is cancelled; if the user
  // navigated away for good the timer fires and wipes the cache.
  useEffect(() => {
    cancelScheduledCacheClear();

    setCachedPresentationFormType(typeForm.getValues());
    setCachedPresentationFormSelections(selectionsForm.getValues());
    setCachedPresentationFormBrands(brandsForm.getValues());

    const typeCacheSub = typeForm.watch((values) => {
      setCachedPresentationFormType(values as ReportTypeStep);
    });
    const selectionsCacheSub = selectionsForm.watch((values) => {
      setCachedPresentationFormSelections(values as DynamicSelectionsStep);
    });
    const brandsCacheSub = brandsForm.watch((values) => {
      setCachedPresentationFormBrands(values as BrandsStep);
    });

    return () => {
      typeCacheSub.unsubscribe();
      selectionsCacheSub.unsubscribe();
      brandsCacheSub.unsubscribe();
      scheduleCacheClear();
    };
  }, [typeForm, selectionsForm, brandsForm]);

  // =============================================================================
  // OTHER HANDLERS
  // =============================================================================

  const isValidStep = useCallback(
    (step: PresentationsTabsPath) => {
      return stepValidity[step];
    },
    [stepValidity]
  );

  const handleOnSubmit = useCallback(() => {
    const data = {
      ...typeForm.getValues(),
      ...selectionsForm.getValues(),
      ...brandsForm.getValues()
    };

    generateReport(data);
  }, [typeForm, selectionsForm, brandsForm, generateReport]);

  return (
    <PresentationFormContext.Provider
      value={{
        type: typeForm,
        selections: selectionsForm,
        brands: brandsForm,
        isValidStep,
        stepValidity,
        handleOnSubmit,
        isSubmitting,
        presentations: presentationsData || [],
        isPresentationsLoading,
        presentationsError,
        currentFormConfig
      }}
    >
      {children}
    </PresentationFormContext.Provider>
  );
};

export const usePresentationFormContext = () => {
  const context = useContext(PresentationFormContext);
  if (!context) {
    throw new Error('usePresentationFormContext must be used within a PresentationFormProvider');
  }
  return context;
};

export default PresentationFormProvider;
